import type { FC } from "react";
import { useState } from "react";
import { Box, Typography, IconButton, Paper } from "@mui/material";

import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TableViewIcon from "@mui/icons-material/TableView";
import { FoodsIcon } from "@foodpilot/foods";

import { useTranslation } from "react-i18next";

import { Loading } from "src/components/utils/Loading";
import { useOrganizationHierarchy } from "src/api/client-api/company/useOrganizationHierarchy";
import { OrganizationChart } from "src/components/OrganizationChart/OrganizationChart";
import { CompanyListContextProvider } from "./CompanyListContext";
import { CompanyGridHeader } from "./CompanyGridHeader";
import { CompanyGrid } from "./CompanyGrid";
import { useNavigateSettingsParent } from "../useNavigateSettingsParent";

type CompaniesListProps = {
    isProvider?: boolean;
};

const CompaniesList = ({ isProvider = false }: CompaniesListProps) => {
    const { t } = useTranslation();
    const navigateSettingsParent = useNavigateSettingsParent();
    const [isHierarchyView, setIsHierarchyView] = useState(false);

    return (
        <CompanyListContextProvider isProvider={isProvider}>
            <Box sx={{ padding: "32px" }}>
                <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid #E7E6E4",
                        borderRadius: "16px",
                        overflow: "hidden",
                        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            padding: "32px",
                            borderBottom: "1px solid #E7E6E4",
                            backgroundColor: "white",
                        }}
                    >
                        <Box
                            sx={{
                                width: "28px",
                                height: "28px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                            }}
                            onClick={navigateSettingsParent}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    "& svg": {
                                        strokeWidth: "2px",
                                        stroke: "currentColor",
                                    },
                                }}
                            >
                                <FoodsIcon icon="arrowLeftShort" size={6} />
                            </Box>
                        </Box>
                        <Typography
                            variant="number-inter"
                            sx={{ lineHeight: "1.6em", color: "#393A36", marginRight: "16px" }}
                        >
                            {isProvider ? t("settings.providers", "Suppliers") : t("settings.companies", "Companies")}
                        </Typography>

                        {/* View Toggle */}
                        <Box sx={{ display: "flex", gap: "8px" }}>
                            <IconButton
                                onClick={() => setIsHierarchyView(!isHierarchyView)}
                                sx={{
                                    padding: "8px",
                                    "&:hover": { backgroundColor: "#F0F0F0" },
                                }}
                            >
                                {isHierarchyView ?
                                    <TableViewIcon />
                                :   <AccountTreeIcon />}
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Search and Actions */}
                    <CompanyGridHeader />

                    {/* Content area */}
                    <Box sx={{ padding: "16px 32px 24px 32px" }}>
                        {isHierarchyView ?
                            <HierarchyView isProvider={isProvider} />
                        :   <CompanyGrid />}
                    </Box>
                </Paper>
            </Box>
        </CompanyListContextProvider>
    );
};

const HierarchyView: FC<{ isProvider?: boolean }> = ({ isProvider }) => {
    const { t } = useTranslation();
    const { rootCompanies, isLoading } = useOrganizationHierarchy();

    if (isLoading) {
        return <Loading />;
    }

    if (!rootCompanies || rootCompanies.length === 0) {
        return (
            <Box sx={{ textAlign: "center", padding: "40px" }}>
                <Typography variant="h6" color="textSecondary">
                    {t("company.noCompaniesFound", "No companies found")}
                </Typography>
            </Box>
        );
    }

    return <OrganizationChart hierarchyData={rootCompanies} />;
};

export { CompaniesList };
